import apiService from './apiService';
import { getActiveTimezone } from '../utils/timezone';

export const calendarService = {
    async listEvents(startDate = null, endDate = null, status = null) {
        const params = new URLSearchParams();
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
        if (status) params.set('status', status);
        return apiService.get(`/api/calendar/events?${params}`);
    },

    async createEvent(eventData) {
        return apiService.post('/api/calendar/events', eventData);
    },

    async updateEvent(eventId, updates) {
        return apiService.patch(`/api/calendar/events/${eventId}`, updates);
    },

    // #532: scope = 'this' | 'following' | 'all' for an occurrence of a repeating event.
    async deleteEvent(eventId, scope) {
        const q = scope && scope !== 'this' ? `?scope=${encodeURIComponent(scope)}` : '';
        return apiService.delete(`/api/calendar/events/${eventId}${q}`);
    },

    async getEvent(eventId) {
        return apiService.get(`/api/calendar/events/${eventId}`);
    },

    // #533 — a package of more than 5 events: its live state, and its ONE
    // Confirm / Cancel ('confirm' | 'cancel').
    async getPackage(packageId) {
        return apiService.get(`/api/calendar/packages/${packageId}`);
    },

    async resolvePackage(packageId, action) {
        return apiService.post(`/api/calendar/packages/${packageId}/resolve`, { action });
    },

    async getActivity(startDate, endDate, displayCurrency = null) {
        const params = new URLSearchParams();
        params.set('start_date', startDate);
        params.set('end_date', endDate);
        if (displayCurrency) params.set('display_currency', displayCurrency);
        // Send the LIVE browser timezone so the BE buckets events by the same
        // local day the user (and PAI) see — otherwise an untimed/TBC event pinned
        // to local midnight is bucketed by its UTC instant and shifts to the
        // previous day when the stored User.timezone is stale/UTC (TBC Placeholder bug).
        try {
            const tz = getActiveTimezone(); // user's reference tz, else browser (#125)
            if (tz) params.set('tz', tz);
        } catch (_) { /* ignore — BE falls back to the stored user timezone */ }
        return apiService.get(`/api/calendar/activity?${params}`);
    },

    // Two-way verified invitations (Agenda V2 §4)
    async inviteToEvent(eventId, { invitee_user_id = null, invitee_email = null }) {
        return apiService.post(`/api/calendar/events/${eventId}/invitations`, {
            invitee_user_id, invitee_email,
        });
    },
    // Main Chat invite — atomically create an event + Bell-invite a resolved list
    // of Omnis users (network contacts + team members) in one action.
    // `force` skips the BE's double-booking guard (#268) — set it only after the
    // user has been shown the clash and chose "Book anyway".
    // #535: `recurrence` (the card's block) makes it a repeating series — the BE
    // re-plans the same occurrences and sends ONE invitation per person.
    async createEventWithInvitations({ title, start_time, end_time = null, location = null, description = null, invitee_user_ids = [], force = false, recurrence = null }) {
        return apiService.post('/api/calendar/events/with-invitations', {
            title, start_time, end_time, location, description, invitee_user_ids,
            ...(recurrence ? { recurrence } : {}),
            ...(force ? { force: true } : {}),
        });
    },
    // #535: invite people to an EXISTING event / the rest of a series
    // (scope this | following | all) — ONE invitation per person.
    async createSeriesInvitations(eventId, { invitee_user_ids = [], scope = 'following' }) {
        return apiService.post(`/api/calendar/events/${eventId}/series-invitations`, {
            invitee_user_ids, scope,
        });
    },
    async respondToInvitation(invitationId, decision) {
        return apiService.post(`/api/calendar/invitations/${invitationId}/respond`, { decision });
    },
    // Invitations awaiting THIS user's answer — the Event Invitations dropdown
    // (#263). The Calendar previously had no discovery surface for incoming
    // invitations: they lived only as Bell rows, so a dismissed or missed Bell
    // left them unreachable.
    async listPendingInvitations() {
        return apiService.get('/api/calendar/invitations/pending');
    },
    // Organizer un-invites a guest — revokes their invitation, clears their Bell
    // row, and deletes the event from their calendar if they'd accepted.
    async revokeInvitation(invitationId) {
        return apiService.delete(`/api/calendar/invitations/${invitationId}`);
    },
};
